import { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, CheckCircle, Zap } from 'lucide-react';

export default function Hero() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40 z-10" />
        <img
          src={`${import.meta.env.BASE_URL}images/hero-main-bg.jpg`}
          alt="Energy Infrastructure"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-6 left-0 w-full z-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className={`
            glass-card rounded-[2rem] px-6 py-3 md:px-8 md:py-4 flex items-center justify-between transition-all duration-500
            ${isScrolled ? 'bg-white/90 border-slate-200 shadow-2xl scale-[1.02]' : 'border-white/40'}
          `}>
            <div className="flex items-center gap-3">
              <a href={`${import.meta.env.BASE_URL}`} className="flex items-center gap-3 group">
                <img
                  src={`${import.meta.env.BASE_URL}images/logo.svg`}
                  alt="CIAC Group"
                  className="h-8 md:h-10 w-auto group-hover:scale-105 transition-all"
                />
                <div className="hidden sm:flex flex-col">
                  <span className={`font-bold tracking-tight text-base md:text-lg leading-none transition-colors ${isScrolled ? 'text-primary' : 'text-white'}`}>CIAC Group</span>
                  <span className={`text-[10px] uppercase tracking-widest mt-1 transition-colors ${isScrolled ? 'text-primary-light' : 'text-white/60'}`}>Energy Solutions</span>
                </div>
              </a>
            </div>

            <div className="hidden md:flex items-center gap-10">
              {['Servicios', 'Nosotros', 'Casos', 'Proyectos'].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase() === 'casos' ? 'casos-exito' : item.toLowerCase()}`}
                  className={`font-semibold transition-all relative group text-sm uppercase tracking-widest ${isScrolled ? 'text-primary' : 'text-white'}`}
                >
                  {item}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isScrolled ? 'bg-accent' : 'bg-white'} transition-all group-hover:w-full`} />
                </a>
              ))}
              <div className="flex items-center gap-4">
                <a
                  href={`${import.meta.env.BASE_URL}platform/login`}
                  className={`text-sm font-black uppercase tracking-widest flex items-center gap-2 px-6 py-2.5 rounded-xl border transition-all ${isScrolled ? 'border-primary/20 text-primary hover:bg-primary hover:text-white' : 'border-white/20 text-white hover:bg-white hover:text-primary'}`}
                >
                  Plataforma
                </a>
                <a
                  href="#contacto"
                  className="btn-accent py-2.5 px-6 rounded-full text-sm shadow-lg shadow-accent/20"
                >
                  Contacto
                </a>
              </div>
            </div>

            <button
              className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-primary' : 'text-white'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 mx-2 glass-card rounded-3xl p-6 flex flex-col gap-6 animate-fade-in border-white/20">
            {['Servicios', 'Nosotros', 'Casos', 'Proyectos'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase() === 'casos' ? 'casos-exito' : item.toLowerCase()}`}
                className="text-primary font-black text-lg uppercase tracking-widest border-b border-primary/5 pb-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <a
              href="#contacto"
              onClick={() => setIsMenuOpen(false)}
              className="btn-accent py-4 rounded-xl text-center"
            >
              Contacto
            </a>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 md:pt-48 md:pb-32 flex flex-col md:flex-row items-center min-h-screen">
        <div className="w-full md:w-3/4 lg:w-2/3 animate-slide-up">
          <div className="badge-premium mb-8 bg-accent/20 text-accent border-accent/20">
            <CheckCircle size={14} />
            <span>Ingeniería Eléctrica de Vanguardia</span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-white mb-8 leading-[1] tracking-tighter">
            Impulsando el <br />
            <span className="text-accent italic">Futuro Industrial</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mb-12 leading-relaxed max-w-2xl font-light">
            Especialistas en soluciones energéticas integrales. Unimos tecnología de punta y sostenibilidad para transformar la infraestructura de su empresa.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="#contacto"
              className="btn-accent text-lg py-5 px-10 rounded-2xl group shadow-2xl shadow-accent/20"
            >
              Iniciar Proyecto
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`${import.meta.env.BASE_URL}platform/login`}
              className="glass-card hover:bg-energy-blue text-white px-10 py-5 rounded-2xl text-lg font-bold transition-all border border-energy-blue/30 flex items-center justify-center backdrop-blur-md group"
            >
              <Zap className="mr-2 text-energy-blue group-hover:text-white transition-colors" size={20} />
              Ver Demo Plataforma
            </a>
            <a
              href="#servicios"
              className="glass-card hover:bg-white/20 text-white px-10 py-5 rounded-2xl text-lg font-bold transition-all border border-white/30 flex items-center justify-center backdrop-blur-md"
            >
              Nuestra Tecnología
            </a>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-12 border-t border-white/10 pt-12">
            {[
              { label: 'Proyectos Entregados', val: '500+' },
              { label: 'Años de Experiencia', val: '15+' },
              { label: 'Certificación SEC', val: 'Clase A' },
            ].map((stat, i) => (
              <div key={i} className="group">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-accent transition-colors">{stat.val}</div>
                <div className="text-white/40 text-xs uppercase tracking-[0.2em] font-bold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>


    </div>
  );
}

